import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useState } from "react";

export interface BulkUploadFileTracker {
    temp_cv_id: string;
    filename: string;
    status?: "processing" | "success" | "failed";
    message?: string;
}

const STORAGE_KEY = "bulk_upload_progress";

interface BulkUploadPersistedState {
    jobId: string;
    batchId: string;
    fileTrackerMap: BulkUploadFileTracker[];
}

export function saveBulkUploadState(state: BulkUploadPersistedState) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore quota errors */ }
}

export function clearBulkUploadState() {
    localStorage.removeItem(STORAGE_KEY);
}

function loadBulkUploadState(): BulkUploadPersistedState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Only restore when there is actually something to show. An empty
        // fileTrackerMap (e.g. all entries were removed/deleted before the
        // refresh) would otherwise render a stuck "Processing 0 items" card.
        if (parsed?.jobId && parsed?.batchId && Array.isArray(parsed?.fileTrackerMap) && parsed.fileTrackerMap.length > 0) {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
}

interface BulkUploadCVContextType {
    jobId: string | null;
    setJobId: Dispatch<SetStateAction<string | null>>;
    pendingFiles: File[];
    setPendingFiles: Dispatch<SetStateAction<File[]>>;
    fileTrackerMap: BulkUploadFileTracker[];
    setFileTrackerMap: Dispatch<SetStateAction<BulkUploadFileTracker[]>>;
    batchId: string | null;
    setBatchId: Dispatch<SetStateAction<string | null>>;
    isVisible: boolean;
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    isProcessing: boolean;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    reloadCount: number;
    setReloadCount: Dispatch<SetStateAction<number>>;
}

export const BulkUploadCVContext = createContext<BulkUploadCVContextType | undefined>(undefined);

interface Props {
    children: ReactNode;
}

export const BulkUploadCVProvider: FC<Props> = ({ children }) => {
    const [jobId, setJobId] = useState<string | null>(null);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [fileTrackerMap, setFileTrackerMap] = useState<BulkUploadFileTracker[]>([]);
    const [batchId, setBatchId] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [reloadCount, setReloadCount] = useState<number>(0);

    // Restore state from localStorage on mount
    useEffect(() => {
        const saved = loadBulkUploadState();
        if (saved) {
            setJobId(saved.jobId);
            setBatchId(saved.batchId);
            setFileTrackerMap(saved.fileTrackerMap);
            setIsProcessing(true);
            setIsVisible(true);
        }
    }, []);

    return (
        <BulkUploadCVContext.Provider value={{
            jobId, setJobId,
            pendingFiles, setPendingFiles,
            fileTrackerMap, setFileTrackerMap,
            batchId, setBatchId,
            isVisible, setIsVisible,
            isProcessing, setIsProcessing,
            reloadCount, setReloadCount,
        }}>
            {children}
        </BulkUploadCVContext.Provider>
    );
}

export const useBulkUploadCVContext = () => {
    const context = useContext(BulkUploadCVContext);
    if (context === undefined) {
        throw new Error("useBulkUploadCVContext must be used within a BulkUploadCVProvider");
    }
    return context;
}
